import React, { useState, useEffect, useRef } from 'react';
import {
  Card, Button, Badge, Spinner, Alert, Form, Row, Col, Collapse,
} from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import ServiceOfferService from '../../../Services/ServiceOfferService';
import OperatorService from '../../../Services/OperatorService';
import { assignableOperators } from '../../../Utils/operatorType';

const STATUS_BADGES = {
  pending: 'secondary',
  sent: 'info',
  received: 'success',
  selected: 'primary',
  rejected: 'danger',
};

function OperatorQuotesPanel({ serviceOfferId, onOfferUpdated }) {
  const [quotes, setQuotes] = useState([]);
  const [operators, setOperators] = useState([]);
  const [selectedOperators, setSelectedOperators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [emailPreviewHtml, setEmailPreviewHtml] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [expandedQuotes, setExpandedQuotes] = useState({});
  const [pasteQuoteId, setPasteQuoteId] = useState(null);
  const [pasteText, setPasteText] = useState('');
  const [applying, setApplying] = useState({});
  const [rejecting, setRejecting] = useState({});
  const [deleting, setDeleting] = useState({});
  const [extracting, setExtracting] = useState({});
  const [selectingQuoteId, setSelectingQuoteId] = useState(null);
  const [markupPercent, setMarkupPercent] = useState(20);
  const [editingQuoteId, setEditingQuoteId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [saving, setSaving] = useState({});
  const typeaheadRef = useRef(null);

  const loadQuotes = async () => {
    if (!serviceOfferId) return;
    setLoading(true);
    try {
      const response = await ServiceOfferService.fetchOperatorQuotes(serviceOfferId);
      setQuotes(response?.data?.data || response?.data || []);
    } catch (err) {
      setError('Failed to load quotes');
    } finally {
      setLoading(false);
    }
  };

  const loadOperators = async () => {
    try {
      const response = await OperatorService.fetchOperators();
      const ops = response?.data?.data || response?.data || [];
      setOperators(ops.filter((op) => op.email));
    } catch {
      // Silently fail
    }
  };

  useEffect(() => {
    loadQuotes();
    loadOperators();
  }, [serviceOfferId]);

  const handleSendInquiry = async () => {
    if (!selectedOperators.length) return;
    setSending(true);
    setError('');
    setSuccess('');
    try {
      const operatorIds = selectedOperators.map((op) => op.Id);
      await ServiceOfferService.sendInquiry(serviceOfferId, operatorIds);
      setSuccess(`Inquiry sent to ${operatorIds.length} operator(s)`);
      setSelectedOperators([]);
      if (typeaheadRef.current) typeaheadRef.current.clear();
      await loadQuotes();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send inquiry');
    } finally {
      setSending(false);
    }
  };

  const handleCopyToClipboard = async () => {
    setPreviewLoading(true);
    try {
      const operatorName = selectedOperators.length === 1
        ? selectedOperators[0].name
        : 'Partner';
      const response = await ServiceOfferService.buildInquiryEmail(serviceOfferId, operatorName);
      const html = response?.data?.data?.html || response?.data?.html || '';
      const htmlBlob = new Blob([html], { type: 'text/html' });
      const textBlob = new Blob([html], { type: 'text/plain' });
      await navigator.clipboard.write([
        new ClipboardItem({ 'text/html': htmlBlob, 'text/plain': textBlob }),
      ]);
      setSuccess('Copied — paste into your email client');
    } catch (err) {
      setError('Failed to copy email');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handlePreviewEmail = async () => {
    setPreviewLoading(true);
    try {
      const operatorName = selectedOperators.length === 1
        ? selectedOperators[0].name
        : 'Partner';
      const response = await ServiceOfferService.buildInquiryEmail(serviceOfferId, operatorName);
      const html = response?.data?.data?.html || response?.data?.html || '';
      setEmailPreviewHtml(html);
      setShowPreview(true);
    } catch (err) {
      setError('Failed to generate preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleApplyQuote = async (quoteId, markup) => {
    setSelectingQuoteId(null);
    setApplying((prev) => ({ ...prev, [quoteId]: true }));
    try {
      const response = await ServiceOfferService.applyQuote(serviceOfferId, quoteId, markup);
      const data = response?.data?.data || response?.data || {};
      if (data.converted) {
        const markupNote = markup > 0 ? ` (${markup}% markup applied)` : '';
        setSuccess(`Quote applied and converted to service offer (${data.tablesGenerated} table(s) generated${markupNote})`);
        if (onOfferUpdated) onOfferUpdated();
      } else if (data.skipReason) {
        setSuccess(`Quote applied but AI conversion skipped: ${data.skipReason}`);
      } else {
        setSuccess('Quote applied successfully');
      }
      await loadQuotes();
    } catch (err) {
      setError('Failed to apply quote');
    } finally {
      setApplying((prev) => ({ ...prev, [quoteId]: false }));
    }
  };

  const handleDeleteQuote = async (quoteId) => {
    setDeleting((prev) => ({ ...prev, [quoteId]: true }));
    try {
      await ServiceOfferService.deleteOperatorQuote(quoteId);
      await loadQuotes();
    } catch (err) {
      setError('Failed to delete quote');
    } finally {
      setDeleting((prev) => ({ ...prev, [quoteId]: false }));
    }
  };

  const handleEditQuote = (quote) => {
    setEditingQuoteId(editingQuoteId === quote.Id ? null : quote.Id);
    setEditFormData({
      operator_price: quote.operator_price ?? '',
      operator_currency: quote.operator_currency || 'EUR',
      price_basis: quote.price_basis || 'total',
      vehicle_information: quote.vehicle_information || '',
      inclusions: quote.inclusions?.join(', ') || '',
      exclusions: quote.exclusions?.join(', ') || '',
      terms: quote.terms || '',
      notes: quote.notes || '',
    });
  };

  const handleSaveEdit = async (quoteId) => {
    setSaving((prev) => ({ ...prev, [quoteId]: true }));
    setError('');
    try {
      await ServiceOfferService.updateOperatorQuote(quoteId, {
        operator_price: editFormData.operator_price !== '' ? Number(editFormData.operator_price) : undefined,
        operator_currency: editFormData.operator_currency || undefined,
        price_basis: editFormData.price_basis || undefined,
        vehicle_information: editFormData.vehicle_information || undefined,
        inclusions: editFormData.inclusions
          ? editFormData.inclusions.split(',').map((s) => s.trim()).filter(Boolean)
          : [],
        exclusions: editFormData.exclusions
          ? editFormData.exclusions.split(',').map((s) => s.trim()).filter(Boolean)
          : [],
        terms: editFormData.terms || undefined,
        notes: editFormData.notes || undefined,
      });
      setEditingQuoteId(null);
      setEditFormData({});
      setSuccess('Quote updated');
      await loadQuotes();
    } catch {
      setError('Failed to update quote');
    } finally {
      setSaving((prev) => ({ ...prev, [quoteId]: false }));
    }
  };

  const handleRejectQuote = async (quoteId) => {
    setRejecting((prev) => ({ ...prev, [quoteId]: true }));
    try {
      await ServiceOfferService.updateOperatorQuote(quoteId, { status: 'rejected' });
      await loadQuotes();
    } catch (err) {
      setError('Failed to reject quote');
    } finally {
      setRejecting((prev) => ({ ...prev, [quoteId]: false }));
    }
  };

  const toggleExpand = (quoteId) => {
    setExpandedQuotes((prev) => ({ ...prev, [quoteId]: !prev[quoteId] }));
  };

  const handlePasteResponse = async (quoteId) => {
    if (!pasteText.trim()) return;
    setExtracting((prev) => ({ ...prev, [quoteId]: true }));
    setError('');
    try {
      const response = await ServiceOfferService.extractQuoteFromEmail(pasteText);
      const extracted = response?.data?.data || response?.data || {};

      await ServiceOfferService.updateOperatorQuote(quoteId, {
        status: 'received',
        received_at: new Date().toISOString(),
        raw_response: pasteText,
        operator_price: extracted.price,
        operator_currency: extracted.currency,
        price_basis: extracted.price_basis,
        vehicle_information: extracted.vehicle_information,
        inclusions: extracted.inclusions || [],
        exclusions: extracted.exclusions || [],
        terms: extracted.terms,
        notes: extracted.notes,
      });

      setPasteQuoteId(null);
      setPasteText('');
      setSuccess('Quote response extracted and saved');
      await loadQuotes();
    } catch (err) {
      setError('Failed to extract quote from email');
    } finally {
      setExtracting((prev) => ({ ...prev, [quoteId]: false }));
    }
  };

  // ASSIGNMENT side: an inquiry asks somebody to QUOTE for doing the work,
  // which is the step before dispatching it to them. A cost bucket cannot
  // quote for a transfer, so it is not offered. (In practice the `op.email`
  // filter in `loadOperators` already hides most of them — that is an
  // accident of the data, not a rule, and this is the rule.)
  const operatorOptions = assignableOperators(operators)
    .filter((op) => !quotes.some((q) => q.operator_id === op.Id))
    .map((op) => ({ ...op, label: `${op.name} (${op.email})` }));

  const rawResponseStyle = {
    maxHeight: '200px',
    overflow: 'auto',
    fontSize: '0.8em',
    whiteSpace: 'pre-wrap',
  };

  return (
    <div>
      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Send Inquiry Section */}
      <Card className="mb-3">
        <Card.Header className="fw-semibold">
          <i className="fa fa-paper-plane me-2" />
          Send Inquiry to Operators
        </Card.Header>
        <Card.Body>
          <Form.Group className="mb-3">
            <Form.Label>Select Operators</Form.Label>
            <Typeahead
              id="operator-select"
              ref={typeaheadRef}
              multiple
              labelKey="label"
              options={operatorOptions}
              selected={selectedOperators}
              onChange={setSelectedOperators}
              placeholder="Search operators by name..."
            />
          </Form.Group>
          <div className="d-flex gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={handleSendInquiry}
              disabled={sending || !selectedOperators.length}
            >
              {sending ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  {' Sending...'}
                </>
              ) : (
                <>
                  <i className="fa fa-envelope me-1" />
                  {' Send via Email'}
                </>
              )}
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleCopyToClipboard}
              disabled={previewLoading || !serviceOfferId}
            >
              <i className="fa fa-clipboard me-1" />
              Copy to Clipboard
            </Button>
            <Button
              variant="outline-info"
              size="sm"
              onClick={handlePreviewEmail}
              disabled={previewLoading || !serviceOfferId}
            >
              {previewLoading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <>
                  <i className="fa fa-eye me-1" />
                  {' Preview'}
                </>
              )}
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Email Preview */}
      <Collapse in={showPreview}>
        <div>
          <Card className="mb-3 border-info">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span className="fw-semibold">Email Preview</span>
              <Button variant="outline-secondary" size="sm" onClick={() => setShowPreview(false)}>
                Close
              </Button>
            </Card.Header>
            <Card.Body style={{ maxHeight: '400px', overflow: 'auto' }}>
              {/* eslint-disable-next-line react/no-danger */}
              <div dangerouslySetInnerHTML={{ __html: emailPreviewHtml }} />
            </Card.Body>
          </Card>
        </div>
      </Collapse>

      {/* Quotes List */}
      <h6 className="fw-bold mt-4 mb-3">
        <i className="fa fa-list me-2" />
        Operator Quotes (
        {quotes.length}
        )
      </h6>

      {loading && <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>}

      {!loading && quotes.length === 0 && (
        <p className="text-muted">No operator quotes yet. Send an inquiry to get started.</p>
      )}

      {quotes.map((quote) => (
        <Card key={quote.Id} className="mb-2">
          <Card.Header className="d-flex justify-content-between align-items-center py-2">
            <div>
              <strong>{quote.operator_name || 'Unknown Operator'}</strong>
              <small className="text-muted ms-2">{quote.operator_email}</small>
              <Badge bg={STATUS_BADGES[quote.status] || 'secondary'} className="ms-2">
                {quote.status}
              </Badge>
            </div>
            <div className="d-flex gap-1">
              {(quote.status === 'received' || quote.status === 'selected' || quote.status === 'rejected') && quote.operator_price && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={
                    () => setSelectingQuoteId(selectingQuoteId === quote.Id ? null : quote.Id)
                  }
                  disabled={applying[quote.Id]}
                >
                  {applying[quote.Id] ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <>
                      <i className="fa fa-check me-1" />
                      {quote.status === 'selected' ? 'Re-apply' : 'Select'}
                    </>
                  )}
                </Button>
              )}
              {(quote.status === 'received' || quote.status === 'selected') && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  title="Edit quote values"
                  onClick={() => handleEditQuote(quote)}
                >
                  <i className="fa fa-pencil" />
                </Button>
              )}
              {quote.status !== 'received' && quote.status !== 'selected' && quote.status !== 'rejected' && (
                <Button
                  variant="outline-success"
                  size="sm"
                  onClick={() => setPasteQuoteId(pasteQuoteId === quote.Id ? null : quote.Id)}
                >
                  <i className="fa fa-paste me-1" />
                  Paste Response
                </Button>
              )}
              {quote.status !== 'selected' && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => handleRejectQuote(quote.Id)}
                  disabled={rejecting[quote.Id]}
                >
                  {rejecting[quote.Id] ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <i className="fa fa-times" />
                  )}
                </Button>
              )}
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => handleDeleteQuote(quote.Id)}
                disabled={deleting[quote.Id]}
              >
                {deleting[quote.Id] ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <i className="fa fa-trash" />
                )}
              </Button>
            </div>
          </Card.Header>

          {/* Quote Details (when received) */}
          {quote.status === 'received' || quote.status === 'selected' || (quote.status === 'rejected' && quote.operator_price) ? (
            <Card.Body className="py-2">
              <Row>
                <Col md={3}>
                  <small className="text-muted d-block">Price</small>
                  <strong>
                    {quote.operator_price
                      ? `${quote.operator_price.toLocaleString()} ${quote.operator_currency || ''}`
                      : '\u2014'}
                  </strong>
                  {quote.price_basis && quote.price_basis !== 'total' && (
                    <small className="text-muted d-block">{quote.price_basis.replace('_', ' ')}</small>
                  )}
                </Col>
                <Col md={3}>
                  <small className="text-muted d-block">Vehicle</small>
                  {quote.vehicle_information || '\u2014'}
                </Col>
                <Col md={3}>
                  <small className="text-muted d-block">Inclusions</small>
                  {quote.inclusions?.length
                    ? quote.inclusions.join(', ')
                    : '\u2014'}
                </Col>
                <Col md={3}>
                  <small className="text-muted d-block">Exclusions</small>
                  {quote.exclusions?.length
                    ? quote.exclusions.join(', ')
                    : '\u2014'}
                </Col>
              </Row>
              {quote.terms && (
                <div className="mt-2">
                  <small className="text-muted d-block">Terms</small>
                  {quote.terms}
                </div>
              )}
              {quote.notes && (
                <div className="mt-2">
                  <small className="text-muted d-block">Notes</small>
                  {quote.notes}
                </div>
              )}
              {quote.raw_response && (
                <div className="mt-2">
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0"
                    onClick={() => toggleExpand(quote.Id)}
                  >
                    {expandedQuotes[quote.Id] ? 'Hide Raw Response' : 'View Raw Response'}
                  </Button>
                  <Collapse in={expandedQuotes[quote.Id]}>
                    <pre className="mt-2 p-2 bg-light border rounded" style={rawResponseStyle}>
                      {quote.raw_response}
                    </pre>
                  </Collapse>
                </div>
              )}
            </Card.Body>
          ) : null}

          {/* Markup & Apply Area */}
          {selectingQuoteId === quote.Id && (
            <Card.Body className="border-top bg-light py-2">
              <Form.Label className="fw-semibold small d-block mb-2">
                Apply markup to operator price
                {quote.operator_price ? (
                  <span className="text-muted fw-normal ms-1">
                    (operator cost:
                    {' '}
                    {quote.operator_price.toLocaleString()}
                    {' '}
                    {quote.operator_currency || 'EUR'}
                    )
                  </span>
                ) : null}
              </Form.Label>
              <div className="d-flex align-items-center gap-3 mb-3">
                <div className="d-flex align-items-center gap-2">
                  <Form.Control
                    type="number"
                    min="0"
                    max="500"
                    style={{ maxWidth: '80px' }}
                    value={markupPercent}
                    onChange={(e) => setMarkupPercent(Number(e.target.value))}
                  />
                  <span className="small text-muted">%</span>
                </div>
                {quote.operator_price && markupPercent > 0 && (
                  <small className="text-success fw-semibold">
                    Client price ≈
                    {' '}
                    {Math.round(quote.operator_price * (1 + markupPercent / 100)).toLocaleString()}
                    {' '}
                    {quote.operator_currency || 'EUR'}
                  </small>
                )}
              </div>
              <div className="d-flex gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleApplyQuote(quote.Id, markupPercent)}
                  disabled={applying[quote.Id]}
                >
                  {applying[quote.Id] ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-1" />
                      Applying...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-check me-1" />
                      Apply
                    </>
                  )}
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setSelectingQuoteId(null)}
                >
                  Cancel
                </Button>
              </div>
            </Card.Body>
          )}

          {/* Edit Quote Area */}
          {editingQuoteId === quote.Id && (
            <Card.Body className="border-top bg-light py-2">
              <p className="fw-semibold small mb-2">Edit quote values:</p>
              <Row className="g-2 mb-2">
                <Col xs={4}>
                  <Form.Label className="small mb-1">Price</Form.Label>
                  <Form.Control
                    type="number"
                    size="sm"
                    value={editFormData.operator_price}
                    onChange={(e) => setEditFormData(
                      (p) => ({ ...p, operator_price: e.target.value }),
                    )}
                  />
                </Col>
                <Col xs={3}>
                  <Form.Label className="small mb-1">Currency</Form.Label>
                  <Form.Select
                    size="sm"
                    value={editFormData.operator_currency}
                    onChange={(e) => setEditFormData(
                      (p) => ({ ...p, operator_currency: e.target.value }),
                    )}
                  >
                    <option value="EUR">EUR</option>
                    <option value="HUF">HUF</option>
                  </Form.Select>
                </Col>
                <Col xs={5}>
                  <Form.Label className="small mb-1">Price Basis</Form.Label>
                  <Form.Select
                    size="sm"
                    value={editFormData.price_basis}
                    onChange={(e) => setEditFormData(
                      (p) => ({ ...p, price_basis: e.target.value }),
                    )}
                  >
                    <option value="total">Total</option>
                    <option value="per_day">Per day</option>
                    <option value="per_bus">Per bus</option>
                    <option value="per_person">Per person</option>
                    <option value="per_km">Per km</option>
                    <option value="per_hour">Per hour</option>
                    <option value="per_route">Per route</option>
                  </Form.Select>
                </Col>
                <Col xs={12}>
                  <Form.Label className="small mb-1">Vehicle</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    value={editFormData.vehicle_information}
                    onChange={(e) => setEditFormData(
                      (p) => ({ ...p, vehicle_information: e.target.value }),
                    )}
                    placeholder="e.g. Mercedes Sprinter, 16 pax"
                  />
                </Col>
                <Col xs={6}>
                  <Form.Label className="small mb-1">Inclusions (comma-separated)</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    value={editFormData.inclusions}
                    onChange={(e) => setEditFormData((p) => ({ ...p, inclusions: e.target.value }))}
                    placeholder="e.g. VAT, driver"
                  />
                </Col>
                <Col xs={6}>
                  <Form.Label className="small mb-1">Exclusions (comma-separated)</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    value={editFormData.exclusions}
                    onChange={(e) => setEditFormData((p) => ({ ...p, exclusions: e.target.value }))}
                    placeholder="e.g. Parking, tolls"
                  />
                </Col>
                <Col xs={12}>
                  <Form.Label className="small mb-1">Terms</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    size="sm"
                    value={editFormData.terms}
                    onChange={(e) => setEditFormData((p) => ({ ...p, terms: e.target.value }))}
                    placeholder="Payment / cancellation terms"
                  />
                </Col>
                <Col xs={12}>
                  <Form.Label className="small mb-1">Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    size="sm"
                    value={editFormData.notes}
                    onChange={(e) => setEditFormData((p) => ({ ...p, notes: e.target.value }))}
                  />
                </Col>
              </Row>
              <div className="d-flex gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleSaveEdit(quote.Id)}
                  disabled={saving[quote.Id]}
                >
                  {saving[quote.Id] ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-1" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-save me-1" />
                      Save
                    </>
                  )}
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setEditingQuoteId(null)}
                >
                  Cancel
                </Button>
              </div>
            </Card.Body>
          )}

          {/* Paste Response Area */}
          {pasteQuoteId === quote.Id && (
            <Card.Body className="border-top bg-light py-2">
              <Form.Group className="mb-2">
                <Form.Label className="fw-semibold small">
                  Paste operator email response below (AI will extract the quote details):
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  value={pasteText}
                  onChange={(e) => setPasteText(e.target.value)}
                  placeholder="Paste the operator's email response here..."
                />
              </Form.Group>
              <div className="d-flex gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handlePasteResponse(quote.Id)}
                  disabled={extracting[quote.Id] || !pasteText.trim()}
                >
                  {extracting[quote.Id] ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-1" />
                      {' Extracting...'}
                    </>
                  ) : (
                    <>
                      <i className="fa fa-magic me-1" />
                      {' Extract & Save'}
                    </>
                  )}
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => { setPasteQuoteId(null); setPasteText(''); }}
                >
                  Cancel
                </Button>
              </div>
            </Card.Body>
          )}
        </Card>
      ))}
    </div>
  );
}

export default OperatorQuotesPanel;
